import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, Check } from 'lucide-react';
import { flushOfflineQueue } from '../../lib/offlineFlush';
import { size as queueSize, checkStorageQuota, pruneOldMutations } from '../../lib/offlineQueue';

/**
 * Bandeau global :
 *   - rouge si offline
 *   - bleu si online + items en queue
 *   - rien sinon
 *
 * Auto-flush au retour online.
 */
export function OfflineBanner(): JSX.Element | null {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [pending, setPending] = useState<number>(0);
  const [flushing, setFlushing] = useState<boolean>(false);

  useEffect(() => {
    const refreshSize = (): void => {
      void queueSize().then(setPending);
    };
    refreshSize();

    // Check quota toutes les 60s ; prune si quota > 80 %
    const quotaTick = async (): Promise<void> => {
      const info = await checkStorageQuota();
      if (info && info.ratio > 0.8) {
        await pruneOldMutations(30);
        refreshSize();
      }
    };
    void quotaTick();
    const id = window.setInterval(refreshSize, 5000);
    const quotaId = window.setInterval(() => void quotaTick(), 60_000);

    const onOnline = async (): Promise<void> => {
      setOnline(true);
      // Auto-flush
      setFlushing(true);
      try {
        await flushOfflineQueue();
      } finally {
        setFlushing(false);
        refreshSize();
      }
    };
    const onOffline = (): void => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.clearInterval(id);
      window.clearInterval(quotaId);
    };
  }, []);

  const handleManualFlush = async (): Promise<void> => {
    setFlushing(true);
    try {
      await flushOfflineQueue();
    } finally {
      setFlushing(false);
      setPending(await queueSize());
    }
  };

  if (online && pending === 0) return null;

  if (!online) {
    return (
      <div className="sticky top-0 z-40 bg-red-50 border-b border-red-200 px-4 py-2 flex items-center gap-3">
        <CloudOff className="w-4 h-4 text-red-600 flex-shrink-0" />
        <p className="text-sm text-red-800 flex-1">
          Mode hors-ligne — tes modifications seront synchronisées au retour du réseau.
        </p>
        {pending > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-medium">
            {pending} en attente
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-40 bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-3">
      {flushing ? (
        <RefreshCw className="w-4 h-4 text-[#0066FF] animate-spin flex-shrink-0" />
      ) : (
        <Check className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
      )}
      <p className="text-sm text-[#0052CC] flex-1">
        {flushing
          ? 'Synchronisation en cours...'
          : `${pending} modification${pending > 1 ? 's' : ''} en attente de sync`}
      </p>
      {!flushing && (
        <button
          onClick={handleManualFlush}
          className="text-xs font-medium px-2 py-1 bg-[#0066FF] text-white rounded hover:bg-[#0052CC]"
        >
          Synchroniser
        </button>
      )}
    </div>
  );
}
